/** The installations bound to this account, and the one thing about them that
 *  can be changed.
 *
 * A source id belongs to the first account that uploads it, forever — that is
 * the sybil defence, and it is why corroboration is counted per account
 * rather than per install. So nothing in this table moves a binding. The name
 * is display only: it exists because "a3f9c2e1b7d4" and "7e01d88f" are the
 * same thing to everybody except the person who owns both, and being able to
 * call one of them "Living room PS5" is the difference between reading this
 * table and guessing at it.
 *
 * Editing is a mode rather than an always-live input. Six rows of open text
 * fields look like a form that needs submitting, and a name that saves on
 * blur saves typos; a Rename button, an input, and an explicit Save is the
 * shape that matches what is happening — one row, one deliberate change.
 */

import { useCallback, useState } from 'react';
import { EmptyState, ErrorNote, Table, TBody, TD, TH, THead, TR } from '@/components/ui';
import { count, relativeTime, shortDate } from '@/lib/format';
import type { Source } from '@/lib/types';

export interface InstallationTableProps {
  sources: readonly Source[];
  /** Saves the new name. Throwing leaves the row in edit with the reason. */
  onRename: (sourceId: string, label: string) => Promise<void>;
}

/** `src/routes/me.ts` refuses anything longer. */
const MAX_LABEL = 60;

export function InstallationTable({ sources, onRename }: InstallationTableProps) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const begin = useCallback((source: Source) => {
    setEditing(source.source_id);
    setDraft(source.label);
    setError(null);
  }, []);

  const cancel = useCallback(() => {
    setEditing(null);
    setError(null);
  }, []);

  const save = useCallback(
    async (sourceId: string) => {
      setSaving(true);
      setError(null);
      try {
        await onRename(sourceId, draft.trim());
        setEditing(null);
      } catch (thrown) {
        setError(thrown);
      } finally {
        setSaving(false);
      }
    },
    [draft, onRename],
  );

  if (sources.length === 0) {
    return (
      <EmptyState title="no installations yet" compact>
        An installation binds itself the first time it uploads. Paste a connection string into the
        datalogger and its source id appears here.
      </EmptyState>
    );
  }

  return (
    <>
      {error ? <ErrorNote error={error} title="could not rename that installation" compact /> : null}
      <Table scroll label="Installations bound to this account">
        <THead>
          <TR>
            <TH>Source id</TH>
            <TH>Name</TH>
            <TH>First seen</TH>
            <TH>Last seen</TH>
            <TH numeric>Uploads</TH>
          </TR>
        </THead>
        <TBody>
          {sources.map((source) => (
            <TR key={source.source_id}>
              <TD mono>{source.source_id}</TD>
              <TD>
                {editing === source.source_id ? (
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <label className="sr-only" htmlFor={`label-${source.source_id}`}>
                      Name for installation {source.source_id}
                    </label>
                    <input
                      id={`label-${source.source_id}`}
                      className="input"
                      style={{ maxWidth: 200 }}
                      value={draft}
                      maxLength={MAX_LABEL}
                      disabled={saving}
                      autoFocus
                      onChange={(event) => setDraft(event.currentTarget.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          void save(source.source_id);
                        }
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          cancel();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ fontSize: 11.5, flex: 'none' }}
                      disabled={saving}
                      onClick={() => void save(source.source_id)}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ fontSize: 11.5, flex: 'none', color: 'var(--color-neutral-500)' }}
                      disabled={saving}
                      onClick={cancel}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <span style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                    <span style={source.label ? undefined : { color: 'var(--color-neutral-600)' }}>
                      {source.label || 'unnamed'}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ fontSize: 11.5, color: 'var(--color-neutral-500)' }}
                      onClick={() => begin(source)}
                    >
                      Rename
                      <span className="sr-only">&nbsp;installation {source.source_id}</span>
                    </button>
                  </span>
                )}
              </TD>
              <TD numeric align="left" color="var(--color-neutral-400)">
                {shortDate(source.first_seen_at)}
              </TD>
              <TD numeric align="left" color="var(--color-neutral-400)">
                {relativeTime(source.last_seen_at)}
              </TD>
              <TD numeric>{count(source.uploads)}</TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </>
  );
}

export default InstallationTable;
